import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NotificationService } from '@app/dialog/services';
import { NewsDetails, NewsSummary } from '@app/news/models';
import { NewsService } from '@app/news/services';
import { FetchNewsList, GetNewsById } from '@app/news/store/news.actions';
import { Action, Selector, State, StateContext, createSelector } from '@ngxs/store';
import { Observable, catchError, of, tap } from 'rxjs';

export interface NewsStateModel {
  news?: NewsSummary[];
  newsDetails: Map<number, NewsDetails>;
}

@State<NewsStateModel>({
  name: 'news',
  defaults: {
    news: undefined,
    newsDetails: new Map<number, NewsDetails>(),
  },
})
@Injectable()
export class NewsState {
  constructor(private newsService: NewsService, private notificationService: NotificationService) {}

  @Selector()
  static getNewsList(state: NewsStateModel): NewsSummary[] | undefined {
    return state.news;
  }

  @Action(FetchNewsList)
  fetchNewsList(ctx: StateContext<NewsStateModel>): Observable<NewsSummary[] | undefined> | undefined {
    const { news } = ctx.getState();

    if (news) {
      return;
    }

    return this.newsService.getNews().pipe(
      tap((data) => {
        const orderedNews = data.sort(
          (a, b) => new Date(b.publishedDate).valueOf() - new Date(a.publishedDate).valueOf()
        );

        ctx.patchState({ news: orderedNews });
      }),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.openAsError(error.error?.Description || 'NEWS_PAGE.FAILED_TO_FETCH_NEWS');
        return of(undefined);
      })
    );
  }

  static getNewsById(id: number): (state: NewsStateModel) => NewsDetails | undefined {
    return createSelector([NewsState], (state: NewsStateModel) => state.newsDetails.get(id));
  }

  @Action(GetNewsById)
  getNewsById(ctx: StateContext<NewsStateModel>, action: GetNewsById): Observable<NewsDetails | undefined> | undefined {
    const { newsDetails } = ctx.getState();
    const id = action.id;

    if (newsDetails.has(id)) {
      return;
    }

    return this.newsService.getNewsDetails(id).pipe(
      tap((data) => {
        newsDetails.set(data.id, data);
        ctx.patchState({ newsDetails: newsDetails });
      }),
      catchError((error: HttpErrorResponse) => {
        this.notificationService.openAsError(error.error?.Description || 'NEWS_PAGE.FAILED_TO_FETCH_NEWS');
        return of(undefined);
      })
    );
  }
}
